import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { NotificationProvider } from "./context/NotificationContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { ThemeProvider } from "./context/ThemeContext";
import { BrandingProvider } from "./context/BrandingContext";
import { Toaster } from "./components/ui/sonner";
import AIChatWidget from "./components/AIChatWidget";
import AccountSuspensionPopup, { SuspensionWarningBanner } from "./components/AccountSuspensionPopup";
import { useActivityTracker } from "./hooks/useActivityTracker";
import { MobileProvider, BottomNavBar } from "./components/MobileLayout";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Apply from "./pages/Apply";
import ProfileSetup from "./pages/ProfileSetup";
import Settings from "./pages/Settings";
import PendingApproval from "./pages/PendingApproval";
import AdminDashboard from "./pages/AdminDashboard";
import OwnerDashboard from "./pages/OwnerDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import AdminDesignTools from "./pages/AdminDesignTools";
import Cart from "./pages/Cart";
import CustomerDashboard from "./pages/customer/Dashboard";
import CustomerRequest from "./pages/customer/RequestService";
import CustomerTracking from "./pages/customer/Tracking";
import CustomerHistory from "./pages/customer/History";
import MechanicDashboard from "./pages/mechanic/Dashboard";
import MechanicJob from "./pages/mechanic/JobDetails";
import ProviderJobPage from "./pages/provider/ProviderJobPage";
import Marketplace from "./pages/marketplace/Marketplace";
import ProductDetail from "./pages/marketplace/ProductDetail";
import SellProduct from "./pages/marketplace/SellProduct";
import InsurancePortal from "./pages/InsurancePortal";
import AppStoreListing from "./pages/AppStoreListing";
import AppDownloadPage from "./pages/AppDownloadPage";
import PlayStoreLaunchPage from "./pages/PlayStoreLaunchPage";
import MyListings from "./pages/marketplace/MyListings";
import EmployeeSettings from "./pages/EmployeeSettings";
import PaymentSuccess from "./pages/PaymentSuccess";
import ProviderDashboard from "./pages/provider/ProviderDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import AdvertiserAuth from "./pages/AdvertiserAuth";
import AdvertiserPortal from "./pages/AdvertiserPortal";
import VendorAuth from "./pages/VendorAuth";
import VendorDashboard from "./pages/VendorDashboard";
import { FoodDeliveryPage, RestaurantDetailPage } from "./pages/FoodDelivery";
import FoodProviderDashboard from "./components/FoodProviderDashboard";
import PriceAlertsDashboard from "./components/PriceAlertsDashboard";
import AIMarketplaceDashboard from "./components/AIMarketplaceDashboard";
import VerificationPopup from "./components/VerificationPopup";
import AutoPartsPage from "./pages/AutoParts";

// Activity Tracker Component
function ActivityTracker() {
  useActivityTracker();
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Emergent OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ActivityTracker />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/pending-approval" element={<ProtectedRoute><PendingApproval /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/owner" element={<ProtectedRoute><OwnerDashboard /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/admin/design" element={<ProtectedRoute><AdminDesignTools /></ProtectedRoute>} />
      <Route path="/admin/price-alerts" element={<ProtectedRoute><PriceAlertsDashboard /></ProtectedRoute>} />
      <Route path="/owner/price-alerts" element={<ProtectedRoute><PriceAlertsDashboard /></ProtectedRoute>} />
      <Route path="/owner/ai-marketplace" element={<ProtectedRoute><AIMarketplaceDashboard /></ProtectedRoute>} />
      <Route path="/admin/ai-marketplace" element={<ProtectedRoute><AIMarketplaceDashboard /></ProtectedRoute>} />
      <Route path="/profile/setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/employee/settings" element={<ProtectedRoute><EmployeeSettings /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/cart" element={<Cart />} />
      
      {/* Marketplace Routes (public) */}
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/product/:productId" element={<ProductDetail />} />
      <Route path="/marketplace/sell" element={<ProtectedRoute><SellProduct /></ProtectedRoute>} />
      <Route path="/marketplace/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
      
      {/* Advertiser Routes (separate auth system) */}
      <Route path="/advertiser/login" element={<AdvertiserAuth />} />
      <Route path="/advertiser/signup" element={<AdvertiserAuth />} />
      <Route path="/advertiser/portal" element={<AdvertiserPortal />} />
      
      {/* Vendor Routes (separate auth system) */}
      <Route path="/vendor/login" element={<VendorAuth />} />
      <Route path="/vendor/signup" element={<VendorAuth />} />
      <Route path="/vendor/dashboard" element={<VendorDashboard />} />
      
      {/* Insurance Company Portal (separate auth system) */}
      <Route path="/insurance" element={<InsurancePortal />} />
      <Route path="/insurance/portal" element={<InsurancePortal />} />
      
      {/* App Download & Store Listing */}
      <Route path="/download" element={<AppDownloadPage />} />
      <Route path="/app" element={<AppStoreListing />} />
      <Route path="/app-store" element={<AppStoreListing />} />
      <Route path="/play-store-launch" element={<PlayStoreLaunchPage />} />
      
      {/* Customer Routes */}
      <Route path="/customer" element={<ProtectedRoute requiredType="customer"><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/customer/request" element={<ProtectedRoute requiredType="customer"><CustomerRequest /></ProtectedRoute>} />
      <Route path="/customer/tracking/:requestId" element={<ProtectedRoute requiredType="customer"><CustomerTracking /></ProtectedRoute>} />
      <Route path="/customer/history" element={<ProtectedRoute requiredType="customer"><CustomerHistory /></ProtectedRoute>} />
      
      {/* Mechanic Routes */}
      <Route path="/mechanic" element={<ProtectedRoute requiredType="mechanic"><MechanicDashboard /></ProtectedRoute>} />
      <Route path="/mechanic/job/:requestId" element={<ProtectedRoute requiredType="mechanic"><MechanicJob /></ProtectedRoute>} />
      
      {/* Provider Dashboard - for mechanics and roadside techs */}
      <Route path="/provider" element={<ProtectedRoute requiredType="mechanic"><ProviderDashboard /></ProtectedRoute>} />
      <Route path="/provider/dashboard" element={<ProtectedRoute requiredType="mechanic"><ProviderDashboard /></ProtectedRoute>} />
      
      {/* Provider Job Page (for all providers) */}
      <Route path="/provider/job/:requestId" element={<ProtectedRoute requiredType="mechanic"><ProviderJobPage /></ProtectedRoute>} />
      
      {/* Admin Job Detail (redirect to provider page for now) */}
      <Route path="/admin/job/:requestId" element={<ProtectedRoute><ProviderJobPage /></ProtectedRoute>} />
      
      {/* Food Delivery Routes */}
      <Route path="/food" element={<FoodDeliveryPage />} />
      <Route path="/food/restaurant/:providerId" element={<RestaurantDetailPage />} />
      <Route path="/food/dashboard" element={<ProtectedRoute requiredType="food_provider"><FoodProviderDashboard /></ProtectedRoute>} />
      
      {/* Auto Parts Marketplace Routes */}
      <Route path="/auto-parts" element={<AutoPartsPage />} />
    </Routes>
    </>
  );
}

// Verification Popup Wrapper - Shows ONLY for providers (not customers) after navigating to provider page
function VerificationWrapperInner() {
  const location = useLocation();
  const [showVerification, setShowVerification] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    if (dismissed) return;
    
    const token = localStorage.getItem('token');
    if (!token) { setShowVerification(false); return; }
    
    const isOnProviderPage = location.pathname.includes('/provider') || 
                             location.pathname.includes('/mechanic') ||
                             location.pathname.includes('/admin') ||
                             location.pathname.includes('/owner');
    
    if (!isOnProviderPage) return;
    
    const checkVerification = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verification/check-required`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.show_popup) {
            setShowVerification(true);
          }
        }
      } catch (error) {
        console.error('Failed to check verification status:', error);
      }
    };
    
    // Small delay so dashboard loads first
    const timer = setTimeout(checkVerification, 1500);
    return () => clearTimeout(timer);
  }, [location.pathname, dismissed]);
  
  if (!showVerification) return null;
  
  return (
    <VerificationPopup 
      isOpen={showVerification} 
      onComplete={() => {
        setShowVerification(false);
        setDismissed(true);
        window.location.reload();
      }} 
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <BrandingProvider>
            <WebSocketProvider>
              <MobileProvider>
                <CartProvider>
                  <NotificationProvider>
                    <SuspensionWarningBanner />
                    <AppRouter />
                    <BottomNavBar />
                    <AccountSuspensionPopup />
                    <AIChatWidget />
                    <VerificationWrapperInner />
                    <Toaster position="top-right" />
                  </NotificationProvider>
                </CartProvider>
              </MobileProvider>
            </WebSocketProvider>
          </BrandingProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
